import type { AudioManager } from '../../audio/AudioManager';
import type { TouchInput, PointerEvent2 } from '../../input/TouchInput';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../../render/Renderer';
import { drawBlade } from '../../render/WeaponRenderer';
import { drawButton, drawMeter, pointInRect } from '../../render/ui/UI';
import type { HeatMap, Rect, WeaponDefinition } from '../../types';
import { createHeatMap } from '../entities/Weapon';
import { BURN_THRESHOLD, scoreHeatZone } from '../systems/Forge';

const HEAT_GAIN_BASE = 0.16;
const COOLING_PER_SEC = 0.12;
const BURN_RATE_PER_SEC = 0.35;

const DRIFT_PERIOD_SEC = 6;
const DRIFT_AMPLITUDE = 0.07;
const BAND_WIDTH = 0.12;

const BELLOWS_RECT: Rect = { x: 16, y: 380, w: LOGICAL_WIDTH - 32, h: 160 };
const STRIKE_BUTTON: Rect = { x: 80, y: 560, w: LOGICAL_WIDTH - 160, h: 56 };

interface ZoneState {
  heat: number;
  baseTargetCenter: number;
  pressT: number;
  taps: number;
}

export class HeatSubphase {
  private heatMap: HeatMap;
  private def: WeaponDefinition;
  private audio: AudioManager;
  private done = false;
  private unbindDown: (() => void) | null = null;
  private fireFlicker = 0;
  private elapsed = 0;
  private burnedFrames = 0;
  private zones: [ZoneState, ZoneState];
  private finalHeatScore = 0;

  constructor(def: WeaponDefinition, input: TouchInput, audio: AudioManager) {
    this.def = def;
    this.audio = audio;
    this.heatMap = createHeatMap(def.heatResolution, 0);
    this.zones = [makeZone(), makeZone()];
    this.unbindDown = input.onPointerDown((e) => this.handleDown(e));
  }

  private handleDown(e: PointerEvent2): void {
    if (this.done) return;
    if (pointInRect(e.pos.x, e.pos.y, STRIKE_BUTTON) && this.anyHeat()) {
      this.complete();
      return;
    }
    if (pointInRect(e.pos.x, e.pos.y, BELLOWS_RECT)) {
      const midX = BELLOWS_RECT.x + BELLOWS_RECT.w / 2;
      const zoneIdx = e.pos.x < midX ? 0 : 1;
      this.pumpBellows(zoneIdx);
    }
  }

  private pumpBellows(zoneIdx: 0 | 1): void {
    const z = this.zones[zoneIdx];
    const headroom = Math.max(0, 1.1 - z.heat);
    const gain = HEAT_GAIN_BASE * Math.pow(headroom, 0.8);
    z.heat = Math.min(1.4, z.heat + gain);
    // Cross-bleed: a fraction of pumped heat leaks to the other zone.
    const other = this.zones[zoneIdx === 0 ? 1 : 0];
    other.heat = Math.min(1.4, other.heat + gain * 0.18);
    z.pressT = 1;
    z.taps++;
    this.audio.play('bellows_tap');
    if (z.heat > BURN_THRESHOLD || other.heat > BURN_THRESHOLD) {
      this.audio.play('burn_warning');
    }
  }

  private anyHeat(): boolean {
    return this.zones[0].heat > 0.1 || this.zones[1].heat > 0.1;
  }

  update(dt: number): void {
    if (this.done) return;
    this.elapsed += dt;
    let anyBurning = false;
    for (const z of this.zones) {
      if (z.heat <= BURN_THRESHOLD) {
        z.heat = Math.max(0, z.heat - COOLING_PER_SEC * dt);
      } else {
        z.heat = Math.min(1.4, z.heat + BURN_RATE_PER_SEC * dt * 0.5);
        anyBurning = true;
      }
      z.pressT = Math.max(0, z.pressT - dt * 5);
    }
    if (anyBurning) this.burnedFrames += Math.round(dt * 60);
    this.writeHeatMap();
    this.fireFlicker = (this.fireFlicker + dt * 6) % (Math.PI * 2);
  }

  /** Map per-zone heats into the per-sample heat map with smooth blending. */
  private writeHeatMap(): void {
    const n = this.heatMap.samples.length;
    const [z0, z1] = this.zones;
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      // Smooth blend: heavily weighted toward the matching zone at the ends,
      // mixes near the middle. Edges cool slightly to simulate fire contact.
      const blend = smoothstep(0.2, 0.8, t);
      const base = z0.heat * (1 - blend) + z1.heat * blend;
      const edgeFalloff = 1 - Math.min(1, Math.abs(t - 0.5) * 0.4);
      this.heatMap.samples[i] = base * edgeFalloff;
    }
  }

  /** Current band centers for each zone, accounting for drift. */
  private targetCenters(): [number, number] {
    const t = this.elapsed;
    return [
      clamp01ish(
        this.zones[0].baseTargetCenter +
          Math.sin((t * Math.PI * 2) / DRIFT_PERIOD_SEC) * DRIFT_AMPLITUDE,
      ),
      clamp01ish(
        this.zones[1].baseTargetCenter +
          Math.cos((t * Math.PI * 2) / (DRIFT_PERIOD_SEC * 1.1) + 0.7) * DRIFT_AMPLITUDE,
      ),
    ];
  }

  isComplete(): boolean {
    return this.done;
  }

  getResult(): {
    heatMap: HeatMap;
    heatScore: number;
    burnedFrames: number;
  } {
    return { heatMap: this.heatMap, heatScore: this.finalHeatScore, burnedFrames: this.burnedFrames };
  }

  private complete(): void {
    const [c0, c1] = this.targetCenters();
    const s0 = scoreHeatZone(this.zones[0].heat, c0, BAND_WIDTH);
    const s1 = scoreHeatZone(this.zones[1].heat, c1, BAND_WIDTH);
    // Slight penalty for highly uneven zones (encourages balancing).
    const evenness = 1 - Math.min(0.3, Math.abs(this.zones[0].heat - this.zones[1].heat) * 0.5);
    this.finalHeatScore = ((s0 + s1) / 2) * evenness;
    this.done = true;
    this.unbindDown?.();
    this.unbindDown = null;
  }

  render(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#0e0a08';
    ctx.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);

    ctx.fillStyle = '#e6d3a4';
    ctx.font = 'bold 20px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('Heat the Blade', LOGICAL_WIDTH / 2, 16);
    ctx.fillStyle = '#a8957a';
    ctx.font = '11px system-ui, sans-serif';
    ctx.fillText('Left bellows = pommel · Right bellows = tip', LOGICAL_WIDTH / 2, 40);
    ctx.fillStyle = '#8a7a60';
    ctx.font = '10px system-ui, sans-serif';
    ctx.fillText('Target bands drift. Settle both into the green.', LOGICAL_WIDTH / 2, 56);

    this.drawForge(ctx);

    drawBlade(ctx, this.def.shape, this.heatMap, {
      cx: LOGICAL_WIDTH / 2,
      cy: 240,
      length: 280,
      rotation: -0.05,
    });

    // Two heat meters, one per zone.
    const [c0, c1] = this.targetCenters();
    const leftMeter: Rect = { x: 14, y: 90, w: 18, h: 220 };
    const rightMeter: Rect = { x: LOGICAL_WIDTH - 32, y: 90, w: 18, h: 220 };
    drawMeter(ctx, {
      rect: leftMeter,
      value: this.zones[0].heat / 1.2,
      optimal: [(c0 - BAND_WIDTH / 2) / 1.2, (c0 + BAND_WIDTH / 2) / 1.2],
      danger: BURN_THRESHOLD / 1.2,
      label: 'POMMEL',
      fillColor: heatBarColor(this.zones[0].heat, c0),
    });
    drawBandTicks(ctx, leftMeter, c0);
    drawMeter(ctx, {
      rect: rightMeter,
      value: this.zones[1].heat / 1.2,
      optimal: [(c1 - BAND_WIDTH / 2) / 1.2, (c1 + BAND_WIDTH / 2) / 1.2],
      danger: BURN_THRESHOLD / 1.2,
      label: 'TIP',
      fillColor: heatBarColor(this.zones[1].heat, c1),
    });
    drawBandTicks(ctx, rightMeter, c1);

    if (this.zones[0].heat > BURN_THRESHOLD || this.zones[1].heat > BURN_THRESHOLD) {
      ctx.fillStyle = '#ff5a3a';
      ctx.font = 'bold 14px system-ui, sans-serif';
      ctx.fillText('BURNING!', LOGICAL_WIDTH / 2, 348);
    }

    this.drawBellows(ctx);

    const canStrike = this.anyHeat();
    drawButton(ctx, {
      rect: STRIKE_BUTTON,
      label: 'TO THE ANVIL',
      enabled: canStrike,
      highlight: this.bothInBand(c0, c1),
    });
  }

  private bothInBand(c0: number, c1: number): boolean {
    const half = BAND_WIDTH / 2;
    return (
      Math.abs(this.zones[0].heat - c0) <= half && Math.abs(this.zones[1].heat - c1) <= half
    );
  }

  private drawBellows(ctx: CanvasRenderingContext2D): void {
    const midX = BELLOWS_RECT.x + BELLOWS_RECT.w / 2;
    const halfW = BELLOWS_RECT.w / 2 - 4;
    const drawHalf = (label: string, x: number, zone: ZoneState): void => {
      const offset = zone.pressT * 4;
      ctx.fillStyle = `rgba(64, 38, 22, ${0.65 + zone.pressT * 0.25})`;
      ctx.fillRect(x, BELLOWS_RECT.y + offset, halfW, BELLOWS_RECT.h - offset);
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#7a5028';
      ctx.strokeRect(x, BELLOWS_RECT.y + offset, halfW, BELLOWS_RECT.h - offset);
      ctx.fillStyle = '#e6c98a';
      ctx.font = 'bold 14px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(label, x + halfW / 2, BELLOWS_RECT.y + 64 + offset);
      ctx.fillStyle = '#a8957a';
      ctx.font = '10px system-ui, sans-serif';
      ctx.fillText(`${zone.taps} pumps`, x + halfW / 2, BELLOWS_RECT.y + 92 + offset);
    };
    drawHalf('POMMEL', BELLOWS_RECT.x, this.zones[0]);
    drawHalf('TIP', midX + 4, this.zones[1]);
    // Center divider.
    ctx.strokeStyle = '#3a2a18';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(midX, BELLOWS_RECT.y);
    ctx.lineTo(midX, BELLOWS_RECT.y + BELLOWS_RECT.h);
    ctx.stroke();
  }

  private drawForge(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#2a2018';
    ctx.fillRect(20, 280, LOGICAL_WIDTH - 40, 90);
    ctx.strokeStyle = '#1a1208';
    ctx.lineWidth = 2;
    ctx.strokeRect(20, 280, LOGICAL_WIDTH - 40, 90);

    const flames = 14;
    for (let i = 0; i < flames; i++) {
      const t = i / (flames - 1);
      const x = 40 + (LOGICAL_WIDTH - 80) * t;
      const phase = this.fireFlicker + i * 0.7;
      const h = 38 + Math.sin(phase) * 8 + Math.sin(phase * 2.3) * 4;
      const w = 14 + Math.cos(phase * 1.7) * 3;
      const grad = ctx.createLinearGradient(x, 360 - h, x, 360);
      grad.addColorStop(0, 'rgba(255, 220, 80, 0.9)');
      grad.addColorStop(0.5, 'rgba(255, 120, 30, 0.7)');
      grad.addColorStop(1, 'rgba(120, 20, 0, 0.0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.ellipse(x, 360 - h * 0.5, w, h * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.strokeStyle = '#3a3a3a';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(40, 320);
    ctx.lineTo(LOGICAL_WIDTH / 2 - 100, 244);
    ctx.stroke();
  }
}

function makeZone(): ZoneState {
  return {
    heat: 0,
    baseTargetCenter: 0.6 + Math.random() * 0.22, // 0.6 – 0.82
    pressT: 0,
    taps: 0,
  };
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function clamp01ish(v: number): number {
  return Math.max(0.4, Math.min(1.0, v));
}

/** Draws two horizontal tick marks at the band edges, overlaid on top of the meter
 *  fill so the target band stays visible even when the fill covers it. */
function drawBandTicks(ctx: CanvasRenderingContext2D, rect: Rect, center: number): void {
  const halfBand = BAND_WIDTH / 2;
  const upperY = rect.y + rect.h * (1 - (center + halfBand) / 1.2);
  const lowerY = rect.y + rect.h * (1 - (center - halfBand) / 1.2);
  const overhang = 3;
  ctx.save();
  ctx.fillStyle = 'rgba(127, 255, 170, 0.45)';
  ctx.fillRect(rect.x - overhang, upperY - 0.5, rect.w + overhang * 2, 1.5);
  ctx.fillRect(rect.x - overhang, lowerY - 0.5, rect.w + overhang * 2, 1.5);
  ctx.restore();
}

function heatBarColor(heat: number, targetCenter: number): string {
  if (heat > BURN_THRESHOLD) return '#ff4a2a';
  if (Math.abs(heat - targetCenter) <= BAND_WIDTH / 2) return '#f0d048';
  if (heat > 0.4) return '#e07028';
  return '#a04020';
}
